import { useState } from "react";

import { AdminAvatar } from "@/components/admin/avatar";
import { Card, CardHead } from "@/components/admin/card";
import { ChipFilter } from "@/components/admin/chip-filter";
import { AdminIcon } from "@/components/admin/icon";
import { Tag } from "@/components/admin/tag";
import { useAuditList } from "@/hooks/use-admin-l4";
import { cn } from "@/lib/cn";

const CATEGORIES = [
	{ k: "", l: "Toutes" },
	{ k: "auth", l: "Auth" },
	{ k: "users", l: "Utilisateurs" },
	{ k: "permissions", l: "Permissions" },
	{ k: "teams", l: "Équipes" },
];

export function AuditTab() {
	const [category, setCategory] = useState<string>("");
	const [result, setResult] = useState<"" | "success" | "failure">("");
	const [page, setPage] = useState(1);
	const pageSize = 50;
	const { data, isLoading } = useAuditList({
		category,
		result: result || undefined,
		page,
		pageSize,
	});

	return (
		<Card>
			<CardHead
				title="Journal d'audit système"
				sub="Conservation 7 ans · chaînage cryptographique inaltérable (CDC §3.4)"
				right={
					<span className="tip">
						<AdminIcon n="shield" s={12} c="var(--a-success)" />
						Hash chain OK
					</span>
				}
			/>

			<div className="toolbar">
				<div className="filter-bar" style={{ marginBottom: 0 }}>
					{CATEGORIES.map((c) => (
						<ChipFilter
							key={c.k}
							on={category === c.k}
							onClick={() => {
								setCategory(c.k);
								setPage(1);
							}}
							data-testid={`audit-cat-${c.k || "all"}`}
						>
							{c.l}
						</ChipFilter>
					))}
					<ChipFilter on={result === ""} onClick={() => setResult("")}>
						Tous résultats
					</ChipFilter>
					<ChipFilter
						on={result === "success"}
						onClick={() => setResult("success")}
					>
						Succès
					</ChipFilter>
					<ChipFilter
						on={result === "failure"}
						onClick={() => setResult("failure")}
					>
						Échecs
					</ChipFilter>
				</div>
				{data && (
					<div style={{ marginLeft: "auto" }} className="muted">
						{data.total} entrée(s)
					</div>
				)}
			</div>

			<div className="table-wrap" style={{ margin: 0 }}>
				<table className="table">
					<thead>
						<tr>
							<th>Horodatage</th>
							<th>Acteur</th>
							<th>Action</th>
							<th>Cible</th>
							<th>Détail</th>
							<th>IP</th>
						</tr>
					</thead>
					<tbody data-testid="audit-tbody">
						{isLoading && (
							<tr>
								<td colSpan={6} className="muted center">
									Chargement…
								</td>
							</tr>
						)}
						{data?.items.length === 0 && (
							<tr>
								<td colSpan={6} className="muted center">
									Aucune entrée pour ces filtres.
								</td>
							</tr>
						)}
						{data?.items.map((a) => (
							<tr key={a.id} data-testid={`audit-row-${a.action}`}>
								<td className="mono" style={{ fontSize: 11 }}>
									{new Date(a.at).toLocaleString("fr-FR")}
								</td>
								<td>
									{a.actorLabel === "system" ? (
										<Tag variant="neutral">Système</Tag>
									) : (
										<div
											style={{ display: "flex", alignItems: "center", gap: 6 }}
										>
											<AdminAvatar
												name={a.actorLabel.split("@")[0] ?? a.actorLabel}
											/>
											<span style={{ fontSize: 12 }}>
												{a.actorLabel.split("@")[0]}
											</span>
										</div>
									)}
								</td>
								<td>
									<Tag
										variant={a.result === "success" ? "info" : "danger"}
										noDot
										className={cn("mono")}
									>
										{a.action}
									</Tag>
								</td>
								<td className="mono" style={{ fontSize: 11 }}>
									{a.target ?? "—"}
								</td>
								<td className="muted" style={{ fontSize: 11 }}>
									{a.detail ?? "—"}
								</td>
								<td className="mono muted" style={{ fontSize: 11 }}>
									{a.ip ?? "—"}
								</td>
							</tr>
						))}
					</tbody>
				</table>
			</div>

			{data && data.total > pageSize && (
				<div className="pagi" style={{ padding: "12px 18px" }}>
					<div>
						Page {page} / {Math.ceil(data.total / pageSize)}
					</div>
					<div className="pages">
						<button
							onClick={() => setPage((p) => Math.max(1, p - 1))}
							disabled={page === 1}
						>
							<AdminIcon n="chevL" s={12} />
						</button>
						<button
							onClick={() =>
								setPage((p) =>
									Math.min(Math.ceil(data.total / pageSize), p + 1),
								)
							}
							disabled={page === Math.ceil(data.total / pageSize)}
						>
							<AdminIcon n="chevR" s={12} />
						</button>
					</div>
				</div>
			)}
		</Card>
	);
}
